import clsx from 'clsx'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { Nullable } from 'primereact/ts-helpers'
import React, { useEffect, useState } from 'react'
import { TreeApi } from 'react-arborist'
import { useSelector } from 'react-redux'
import { ResursTreeApi } from '../../../services/modules/tree'
import { RootState } from '../../../services/types/store-types'
import styles from './create-modal.module.css'

type TListItem = {
	name: string
	className: string
	type?: string
	group?: string
	filter?: string[]
}

const getTitleByTree = (tree: ResursTreeApi.treeName) => {
	switch (tree) {
		case 'device':
			return 'Добавить устройство'
		case 'user':
			return 'Добавить абонента'
		case 'operator':
			return 'Добавить оператора'
	}
}

export const ResursCreateModal = React.forwardRef(
	(
		props: {
			show: boolean
			setShow: (value: boolean) => void
			createModalParams: ResursTreeApi.TGetTreeObjectListAvailableToAddResponse
			tree: ResursTreeApi.treeName
		},
		ref: React.Ref<TreeApi<any>>
	) => {
		const { show, setShow, createModalParams, tree } = props

		const filterOptions = createModalParams.filterList
		const [filter, setFilter] = useState<{
			id: string
			type: string
		} | null>(null)

		const [listOptions, setListOptions] = useState(createModalParams.list)
		const [listSelected, setListSelected] = useState<TListItem | null>(null)

		useEffect(() => {
			if (filterOptions) {
				setFilter(filterOptions[0])
			}
			if (listOptions) {
				setListSelected(listOptions[0])
			}
		}, [filterOptions])

		const [groupList, setGroupList] = useState<any>(null)

		useEffect(() => {
			if (
				createModalParams.list.length > 0 &&
				createModalParams.list[0].group
			) {
				const groupedData: any = {}
				// Проходим по каждому элементу в массиве данных
				createModalParams.list.forEach(item => {
					// Если ключа с названием типа (item.type) еще нет в groupedData, создаем его
					if (item.type && !groupedData[item.type]) {
						groupedData[item.type] = {
							label: item.group,
							code: item.type,
							items: [],
						}
					}
					// Добавляем текущий элемент в массив items для соответствующего типа
					item.type && groupedData[item.type].items.push(item)
				})

				type TListGroup = {
					label: string
					code: string
					items: Array<TListItem>
				}

				// Преобразуем объект groupedData в массив, содержащий только его значения
				const result = Object.values<TListGroup>(groupedData)
				setGroupList(result)
				setListSelected(result[0].items[0])
			}
		}, [createModalParams.list])

		useEffect(() => {
			if (filter) {
				const newListOptions = createModalParams.list.filter(opt => {
					if (opt.filter && opt.filter.includes(filter.id)) {
						return opt
					}
					return null
				})
				setListOptions(newListOptions)
			}
		}, [createModalParams.list, filter])

		useEffect(() => {
			setListSelected(listOptions[0])
		}, [listOptions])

		const [count, setCount] = useState<Nullable<number>>(1)
		const [checked, setChecked] = useState<boolean>(true)
		const lastSelect = useSelector(
			(state: RootState) => state.tree.selectedNodes
		)
		const handleSubmit = async () => {
			if (listSelected) {
				setShow(false)
				try {
					const response = await ResursTreeApi.createObject({
						tree: tree,
						className: listSelected.className,
						parentId: lastSelect.objectIds,
						objType: listSelected.type!,
						activate: checked,
						count: count!,
					})

					if (response) {
						if (ref && 'current' in ref && ref.current) {
							const parentNode = ref.current.get(
								lastSelect.objectIds[0] + '.' + tree
							)

							parentNode?.open()
							const addedNode = ref.current.get(response.objectId + '.' + tree)
							addedNode?.select()
						}
					}
				} catch (error: any) {
					console.error(error.message)
				}
			}
		}

		useEffect(() => {
			const observer = new MutationObserver(() => {
				const dropdownItems = document.querySelector('.p-dropdown-items')
				if (dropdownItems) {
					dropdownItems.setAttribute('data-at-wc-select-list', 'true')
				}
			})

			observer.observe(document.body, {
				childList: true,
				subtree: true,
			})

			return () => {
				observer.disconnect()
			}
		}, [])

		const isFormValid = listSelected !== null && count != null && count > 0

		return (
			<Dialog
				dismissableMask
				header={getTitleByTree(tree)}
				visible={show}
				style={{
					width: 460,
				}}
				onHide={() => {
					setShow(false)
				}}
			>
				<form
					data-at-wc-create-modal-form
					onSubmit={e => {
						e.preventDefault()
						handleSubmit()
					}}
					style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
				>
					{createModalParams.filterListCaption && (
						<div className={'p-inputgroup flex-1'}>
							<span
								className={clsx(
									'p-inputgroup-addon',
									styles.resurs__props__field_label
								)}
								style={{
									cursor: 'default',
								}}
							>
								{createModalParams.filterListCaption}
							</span>

							<Dropdown
								data-at-wc-create-modal-filter-dropdown
								className={clsx(styles.resurs__props__field, styles.dropdown)}
								value={filter}
								onChange={e => setFilter(e.value)}
								options={filterOptions}
								optionLabel='type'
							/>
						</div>
					)}

					{createModalParams.listCaption && (
						<div className={'p-inputgroup flex-1'}>
							<span
								className={clsx(
									'p-inputgroup-addon',
									styles.resurs__props__field_label
								)}
								style={{
									cursor: 'default',
								}}
							>
								{createModalParams.listCaption}
							</span>

							{createModalParams.list[0].group ? (
								<Dropdown
									filter={listOptions.length > 8 ? true : false}
									value={listSelected}
									data-at-wc-create-modal-group-list-dropdown
									onChange={e => setListSelected(e.value)}
									options={groupList}
									scrollHeight='350px'
									optionGroupLabel='label'
									optionLabel='name'
									className={clsx(styles.resurs__props__field, styles.dropdown)}
									emptyFilterMessage='Поиск не дал результатов'
								/>
							) : (
								<Dropdown
									filter={listOptions.length > 8 ? true : false}
									value={listSelected}
									onChange={e => setListSelected(e.value)}
									options={listOptions.sort((a, b) =>
										a.name.localeCompare(b.name)
									)}
									data-at-wc-create-modal-list-options-dropdown
									panelClassName='data-at-wc-create-modal-list-options-dropdown'
									scrollHeight='350px'
									optionLabel='name'
									className={clsx(styles.resurs__props__field, styles.dropdown)}
									emptyFilterMessage='Поиск не дал результатов'
								/>
							)}
						</div>
					)}

					<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
						<div className={'p-inputgroup flex-1'}>
							<span
								className={clsx(
									'p-inputgroup-addon',
									styles.resurs__props__field_label
								)}
								style={{
									cursor: 'default',
								}}
							>
								Количество:
							</span>
							<InputText
								value={count ? String(count) : ''}
								className={styles.inputText}
								data-at-wc-create-modal-number-input
								maxLength={3}
								keyfilter={'pint'}
								max={999}
								onChange={e =>
									setCount(e.target.value ? parseInt(e.target.value) : null)
								}
							/>
						</div>
						{!isFormValid && (
							<small className='p-error' style={{ alignSelf: 'flex-end' }}>
								Это поле обязательно.
							</small>
						)}
					</div>

					<div style={{ display: 'flex', justifyContent: 'space-between' }}>
						{tree === 'device' && (
							<div className={styles.activate}>
								<Checkbox
									onChange={e => {
										e.checked !== undefined && setChecked(e.checked)
									}}
									data-at-wc-create-modal-checkbox
									checked={checked}
								/>
								<label>Активировать</label>
							</div>
						)}
						<Button
							data-at-wc-create-modal-submit
							label='OK'
							autoFocus
							size='small'
							style={{
								marginLeft: 'auto',
							}}
							disabled={!isFormValid}
						></Button>
					</div>
				</form>
			</Dialog>
		)
	}
)
